import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    AlertTriangle, ClipboardList, ExternalLink,
    RefreshCw, Search, X, ArrowUpDown, Calendar, ShoppingCart,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import classNames from "classnames";
import CommerceItemImage from "../../../../components/store/CommerceItemImage.jsx";
import { useCartContext } from "../../../../components/providers/CartProvider.jsx";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";
import DropdownOrder from "../../../Store/DropdownOrder.jsx";
import { editorPathForId } from "../../../../products/registry";

const COMMERCIAL_BADGE = {
    solicitado:          "badge-warning",
    en_revision:         "badge-info",
    presupuesto_enviado: "badge-secondary",
    aceptado:            "badge-success",
    rechazado:           "badge-error",
    incidencia:          "badge-error",
};

const PRODUCTION_BADGE = {
    no_iniciado:   "badge-ghost",
    preparacion:   "badge-info",
    en_produccion: "badge-info",
    entregado:     "badge-success",
    bloqueado:     "badge-error",
};

const COMMERCIAL_LABELS = {
    solicitado:          "Solicitado",
    en_revision:         "En revisión",
    presupuesto_enviado: "Presupuesto enviado",
    aceptado:            "Aceptado",
    rechazado:           "Rechazado",
    incidencia:          "Incidencia",
};

const PRODUCTION_LABELS = {
    no_iniciado:   "No iniciado",
    preparacion:   "Preparación",
    en_produccion: "En producción",
    entregado:     "Entregado",
    bloqueado:     "Bloqueado",
};

const PURCHASE_LABELS = {
    no_disponible:     "Pendiente de propuesta",
    lista_para_compra: "Lista para compra",
    en_carrito:        "Añadida al carrito",
    comprada:          "Comprada",
    caducada:          "Propuesta caducada",
    cancelada:         "Propuesta cancelada",
};

const COMMERCIAL_HINT = {
    solicitado:          "Revisaremos tu solicitud y nos pondremos en contacto contigo.",
    en_revision:         "Estamos revisando los detalles de tu pedido.",
    presupuesto_enviado: "Tu presupuesto está disponible. Puedes aceptarlo aquí o desde el email.",
    aceptado:            "¡Pedido aceptado! Pasará a producción próximamente.",
    rechazado:           "No pudimos aceptar este pedido. Contáctanos si tienes dudas.",
    incidencia:          "Hay una incidencia con este pedido. Nos pondremos en contacto.",
};

const fmtCurrency = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(value || 0));

const SORT_OPTIONS = [
    { nombre: "Fecha",  valor: "created_at",       icon: <Calendar    className="w-4 h-4" /> },
    { nombre: "Estado", valor: "commercial_status", icon: <ArrowUpDown className="w-4 h-4" /> },
];

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

export default function UserCustomOrderRequests() {
    const { fetchCart } = useCartContext();
    const [requests, setRequests]   = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode]   = useState({ sortBy: "created_at", sortDirection: "desc" });
    const [acceptingRequestId, setAcceptingRequestId] = useState(null);
    const [proposalErrors, setProposalErrors] = useState({});

    const debouncedSearch = useDebounce(searchTerm, 300);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get("/custom-order-requests/");
            const results = data.results ?? data;
            setRequests(results);
            setTotalCount(data.count ?? results.length);
        } catch (err) {
            console.error("Error fetching user custom order requests:", err);
            setError(err.response?.data?.detail || "No se pudieron cargar tus solicitudes.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    const handleAcceptProposal = async (requestId) => {
        setAcceptingRequestId(requestId);
        setProposalErrors((current) => ({ ...current, [requestId]: null }));
        try {
            const { data } = await axios.post(
                `/custom-order-requests/${requestId}/add-to-cart/`,
                {},
                { withCredentials: true },
            );
            setRequests((current) => current.map((item) => (
                item.id === requestId ? data.request : item
            )));
            await fetchCart();
            toast.success("Presupuesto aceptado y añadido al carrito.");
        } catch (err) {
            const message = err.response?.data?.detail || "No se pudo aceptar el presupuesto.";
            setProposalErrors((current) => ({ ...current, [requestId]: message }));
        } finally {
            setAcceptingRequestId(null);
        }
    };

    const filtered = useMemo(() => {
        let list = [...requests];
        if (debouncedSearch) {
            const q = debouncedSearch.toLowerCase();
            list = list.filter(
                (r) =>
                    (r.customized_keyring_detail?.name || "").toLowerCase().includes(q) ||
                    (r.contact_email || "").toLowerCase().includes(q) ||
                    (COMMERCIAL_LABELS[r.commercial_status] || "").toLowerCase().includes(q)
            );
        }
        list.sort((a, b) => {
            const dir = viewMode.sortDirection === "desc" ? -1 : 1;
            if (viewMode.sortBy === "created_at") {
                return dir * (new Date(a.created_at) - new Date(b.created_at));
            }
            if (viewMode.sortBy === "commercial_status") {
                return dir * (a.commercial_status || "").localeCompare(b.commercial_status || "");
            }
            return 0;
        });
        return list;
    }, [requests, debouncedSearch, viewMode]);

    const showEmptyState   = !loading && !error && filtered.length === 0;
    const hasActiveFilters = debouncedSearch || viewMode.sortBy !== "created_at";
    const centerContent    = loading || showEmptyState || !!error;

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">

            {/* ── Header de controles ─────────────────────────────────────── */}
            <div className="glassCard p-4 z-40">
                {/* Título + contador + refresh */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <ClipboardList className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Mis solicitudes</h2>
                            <p className="text-sm text-base-content/60">
                                {totalCount} {totalCount === 1 ? "solicitud" : "solicitudes"}
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={fetchRequests}
                        title="Actualizar"
                        disabled={loading}
                    >
                        {loading
                            ? <span className="loading loading-spinner loading-xs" />
                            : <RefreshCw className="w-4 h-4" />
                        }
                    </button>
                </div>

                {/* Búsqueda + orden */}
                <div className="flex flex-col-reverse lg:flex-row gap-2">
                    <div className="flex-1 lg:flex-initial min-w-64 lg:min-w-48 lg:max-w-md">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            className="w-full"
                            placeholder="Buscar por modelo, email o estado…"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-row lg:items-center lg:gap-2">
                        <DropdownOrder
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            arrayDatosOrdenacion={SORT_OPTIONS}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Pills de filtros activos */}
                {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {debouncedSearch && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                                <Search className="w-3 h-3 shrink-0" />
                                <span className="max-w-[160px] truncate">"{debouncedSearch}"</span>
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Quitar filtro de búsqueda"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}
                        {viewMode.sortBy !== "created_at" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-base-300 text-base-content/70 border border-base-300">
                                <ArrowUpDown className="w-3 h-3 shrink-0" />
                                <span>{SORT_OPTIONS.find((o) => o.valor === viewMode.sortBy)?.nombre ?? "Orden"}</span>
                                <button
                                    onClick={() => setViewMode((prev) => ({ ...prev, sortBy: "created_at", sortDirection: "desc" }))}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Restablecer orden"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Contenido principal ─────────────────────────────────────── */}
            <div className={classNames(
                "h-full flex-1 overflow-y-auto scrollbar_mod glassCard",
                centerContent ? "flex items-center justify-center" : "p-3"
            )}>

                {/* Cargando */}
                {loading && (
                    <div className="text-center py-12">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                )}

                {/* Error */}
                {error && !loading && (
                    <div className="text-center py-8">
                        <div role="alert" className="alert alert-error max-w-lg shadow-lg">
                            <AlertTriangle className="w-5 h-5" />
                            <div>
                                <h3 className="font-semibold">No se pudieron cargar tus solicitudes</h3>
                                <div className="text-sm">{error}</div>
                            </div>
                            <button className="btn btn-sm btn-ghost" onClick={fetchRequests}>
                                Reintentar
                            </button>
                        </div>
                    </div>
                )}

                {/* Estado vacío */}
                {showEmptyState && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-6 bg-base-200 rounded-full flex items-center justify-center">
                            <ClipboardList className="w-10 h-10 text-base-content/30" />
                        </div>
                        {debouncedSearch ? (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No se encontraron solicitudes
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    No hay solicitudes que coincidan con "{debouncedSearch}"
                                </p>
                                <button onClick={() => setSearchTerm("")} className="btn btn-outline">
                                    Limpiar búsqueda
                                </button>
                            </>
                        ) : (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    Todavía no has enviado solicitudes
                                </h3>
                                <p className="text-base-content/60 mb-2 max-w-xs mx-auto">
                                    Abre uno de tus diseños y pulsa <strong>Pedir este diseño</strong> para solicitar una tirada.
                                </p>
                                <Link to="/profile/modelos_creados" className="btn btn-primary mt-4">
                                    Ir a mis diseños
                                </Link>
                            </>
                        )}
                    </div>
                )}

                {/* Grid de solicitudes */}
                {!loading && !error && filtered.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {filtered.map((request) => (
                            <article
                                key={request.id}
                                className="rounded-lg bg-base-100/70 border border-base-300 p-4"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <CommerceItemImage
                                            customKeyring={request.customized_keyring_detail}
                                            alt={request.customized_keyring_detail?.name || `Modelo #${request.customized_keyring}`}
                                            className="w-14 h-14"
                                        />
                                        <div className="min-w-0">
                                            <h3 className="font-semibold">
                                                {request.customized_keyring_detail?.name || `Modelo #${request.customized_keyring}`}
                                            </h3>
                                            <p className="text-xs text-base-content/50">
                                                Enviada el {new Date(request.created_at).toLocaleDateString("es-ES")}
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        className="btn btn-xs btn-ghost gap-1 flex-shrink-0"
                                        to={editorPathForId(request.customized_keyring)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        Abrir
                                    </Link>
                                </div>

                                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="block text-xs text-base-content/50">Cantidad</span>
                                        {request.quantity} uds.
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Material</span>
                                        {request.material}
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Color</span>
                                        {request.color}
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Contacto</span>
                                        {request.contact_email}
                                    </div>
                                    {request.finish && (
                                        <div>
                                            <span className="block text-xs text-base-content/50">Acabado</span>
                                            {request.finish}
                                        </div>
                                    )}
                                    {request.desired_date && (
                                        <div>
                                            <span className="block text-xs text-base-content/50">Fecha deseada</span>
                                            {new Date(request.desired_date).toLocaleDateString("es-ES")}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    <span className={`badge ${COMMERCIAL_BADGE[request.commercial_status] ?? "badge-ghost"}`}>
                                        {COMMERCIAL_LABELS[request.commercial_status] || request.commercial_status}
                                    </span>
                                    <span className={`badge badge-outline ${PRODUCTION_BADGE[request.production_status] ?? "badge-ghost"}`}>
                                        {PRODUCTION_LABELS[request.production_status] || request.production_status}
                                    </span>
                                </div>

                                {COMMERCIAL_HINT[request.commercial_status] && (
                                    <p className="mt-2 text-xs text-base-content/60">
                                        {COMMERCIAL_HINT[request.commercial_status]}
                                    </p>
                                )}

                                {request.approved_final_price && (
                                    <div className="mt-4 rounded-lg border border-success/30 bg-success/10 p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-sm font-semibold">
                                                {PURCHASE_LABELS[request.purchase_status] || request.purchase_status}
                                            </span>
                                            <span className="text-lg font-bold">
                                                {fmtCurrency(request.approved_final_price, request.approved_currency)}
                                            </span>
                                        </div>
                                        {request.proposal_valid_until && (
                                            <p className="mt-1 text-xs text-base-content/60">
                                                Válida hasta {new Date(request.proposal_valid_until).toLocaleDateString("es-ES")}
                                            </p>
                                        )}
                                        {request.proposal_message && (
                                            <p className="mt-2 text-sm whitespace-pre-wrap text-base-content/70">
                                                {request.proposal_message}
                                            </p>
                                        )}
                                        {request.purchase_status === "lista_para_compra" && (
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm mt-3 w-full gap-2"
                                                onClick={() => handleAcceptProposal(request.id)}
                                                disabled={acceptingRequestId === request.id}
                                            >
                                                {acceptingRequestId === request.id ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : (
                                                    <ShoppingCart className="h-4 w-4" />
                                                )}
                                                Aceptar y añadir al carrito
                                            </button>
                                        )}
                                        {request.purchase_status === "en_carrito" && (
                                            <Link to="/cart" className="btn btn-success btn-sm mt-3 w-full gap-2">
                                                <ShoppingCart className="h-4 w-4" />
                                                Ir al carrito
                                            </Link>
                                        )}
                                        {proposalErrors[request.id] && (
                                            <p role="alert" className="mt-2 text-xs text-error">
                                                {proposalErrors[request.id]}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {request.notes && (
                                    <p className="mt-3 text-sm whitespace-pre-wrap text-base-content/70">
                                        {request.notes}
                                    </p>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
